/**
 * The calm layout's few parts (#1717), from the v6 prototype's CSS:
 * `.sec5` sections split by a hairline, `.lk` quiet text links, status as a
 * coloured word, and one drawer for everything that used to be a card.
 *
 * Rules (Corbin, 2026-10-01): one filled button per screen (the Finish Line's
 * Next — nothing here adds another), quiet links everywhere else, no cards
 * inside cards, text at least 13px.
 */
import { forwardRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

export const FOCUS = "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const LINK = "text-[14px] font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:no-underline";
const MUTE = "text-[14px] font-medium text-muted-foreground hover:text-foreground hover:underline";
const DANGER = "text-[14px] font-medium text-red-700 hover:underline dark:text-red-400";

type Tone = "link" | "mute" | "danger";
const toneCls = (t: Tone) => (t === "mute" ? MUTE : t === "danger" ? DANGER : LINK);

/** A quiet text link that is really a button. */
export function Quiet({
    children,
    onClick,
    tone = "link",
    disabled,
    title,
    className,
    ...rest
}: {
    children: ReactNode;
    onClick?: () => void;
    tone?: Tone;
    disabled?: boolean;
    title?: string;
    className?: string;
    "aria-expanded"?: boolean;
    "aria-controls"?: string;
    "aria-label"?: string;
    "data-testid"?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={cn(toneCls(tone), FOCUS, "bg-transparent p-0", className)}
            {...rest}
        >
            {children}
        </button>
    );
}

/** The same look for a real link (in-app or external). */
export function QuietLink({
    to,
    href,
    children,
    tone = "link",
    external,
}: {
    to?: string;
    href?: string;
    children: ReactNode;
    tone?: Tone;
    external?: boolean;
}) {
    const cls = cn(toneCls(tone), FOCUS);
    if (to) {
        return (
            <Link to={to} className={cls}>
                {children}
            </Link>
        );
    }
    return (
        <a href={href} className={cls} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}>
            {children}
        </a>
    );
}

export const TONE_INK = {
    pass: "text-emerald-700 dark:text-emerald-400",
    fail: "text-red-700 dark:text-red-400",
    warn: "text-amber-700 dark:text-amber-400",
    todo: "text-muted-foreground",
} as const;

export function Word({
    tone,
    children,
    className,
}: {
    tone: keyof typeof TONE_INK;
    children: ReactNode;
    className?: string;
}) {
    return <span className={cn("text-[13.5px] font-semibold", TONE_INK[tone], className)}>{children}</span>;
}

/** A work-sheet section: title, a gray summary, quiet links on the right. */
export const Section = forwardRef<
    HTMLElement,
    {
        id: string;
        title: string;
        summary?: ReactNode;
        actions?: ReactNode;
        children?: ReactNode;
        testId?: string;
    }
>(function Section({ id, title, summary, actions, children, testId }, ref) {
    return (
        <section
            ref={ref}
            id={id}
            aria-labelledby={`${id}-h`}
            className="scroll-mt-40 border-t pb-1.5 pt-5 first:border-t-0"
            data-testid={testId ?? id}
        >
            <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 id={`${id}-h`} className="m-0 text-[17px] font-bold leading-snug text-foreground">
                    {title}
                </h2>
                {summary && <span className="text-[14px] text-muted-foreground">{summary}</span>}
                {actions && <span className="ml-auto flex flex-wrap items-baseline gap-x-4 gap-y-1">{actions}</span>}
            </div>
            {children}
        </section>
    );
});

/** An empty section is ONE line: "Parts & PO · none ordered". */
export function OneLine({
    id,
    title,
    words,
    actions,
}: {
    id: string;
    title: string;
    words: string;
    actions?: ReactNode;
}) {
    return (
        <section
            id={id}
            className="scroll-mt-40 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t py-4 first:border-t-0"
            data-testid={id}
            data-empty="true"
        >
            <h2 className="m-0 text-[17px] font-bold leading-snug text-foreground">{title}</h2>
            <span className="text-[14px] text-muted-foreground">· {words}</span>
            {actions && <span className="ml-auto flex gap-x-4">{actions}</span>}
        </section>
    );
}

/** A ledger row (`.lr5`): what · amount · state · action. */
export function LedgerRow({
    what,
    sub,
    amount,
    state,
    action,
    nested,
    total,
    testId,
}: {
    what: ReactNode;
    sub?: ReactNode;
    amount?: ReactNode;
    state?: ReactNode;
    action?: ReactNode;
    nested?: boolean;
    total?: boolean;
    testId?: string;
}) {
    return (
        <div
            className={cn(
                "grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3.5 gap-y-0.5 py-2 text-[14.5px] sm:grid-cols-[minmax(0,1fr)_110px_140px_auto]",
                nested ? "py-1 pl-3.5 text-[13.5px] text-foreground/80" : "border-t first:border-t-0",
                total && "border-t border-foreground/20 font-bold",
            )}
            data-testid={testId}
        >
            <span className="min-w-0">
                {what}
                {sub && <span className="block text-[13px] text-muted-foreground">{sub}</span>}
            </span>
            <span className="text-right font-semibold tabular-nums">{amount}</span>
            <span className="col-start-2 text-right text-[13.5px] text-muted-foreground sm:col-start-auto sm:text-left">
                {state}
            </span>
            <span className="col-span-2 flex flex-wrap gap-x-3.5 sm:col-span-1 sm:justify-end">{action}</span>
        </div>
    );
}

/**
 * Job page v2 (slice 4): what used to open in a drawer opens right where you
 * clicked. The existing card is mounted as-is (every button it had still
 * works); only its own box is flattened, so there is no box inside a box.
 */
export const InPlace = forwardRef<
    HTMLDivElement,
    {
        id: string;
        title: string;
        onClose: () => void;
        children: ReactNode;
        testId?: string;
    }
>(function InPlace({ id, title, onClose, children, testId }, ref) {
    return (
        <div
            ref={ref}
            id={id}
            className="scroll-mt-40 border-t pb-2 pt-3"
            data-testid={testId ?? id}
            role="region"
            aria-label={title}
        >
            <div className="mb-1 flex items-baseline gap-3">
                <h3 className="m-0 text-[15px] font-bold text-foreground">{title}</h3>
                <Quiet tone="mute" className="ml-auto" onClick={onClose} data-testid={`${testId ?? id}-close`}>
                    Close ▴
                </Quiet>
            </div>
            <div className="space-y-3 [&>*]:rounded-none [&>*]:border-0 [&>*]:bg-transparent [&>*]:shadow-none [&_.rounded-xl.border]:border-0 [&_.rounded-xl.border]:shadow-none">
                {children}
            </div>
        </div>
    );
});
